import { useCriticalRead } from '../interaction/readiness';
import React, { useEffect, createContext, useContext, useMemo } from 'react';
import { createPreference } from '../interaction/runtime';
import { useRouteActive } from '../interaction/runtime';
import type { DropdownItem } from '../primitives/widgets/dropdown';

import Decimal from 'decimal.js';
import {MONEY_DECIMALS,PRICE_DECIMALS,SALE_PRICE_DECIMALS,PRICE_MAXIMUM} from './priceRules';
export {MONEY_DECIMALS,PRICE_DECIMALS,SALE_PRICE_DECIMALS,PRICE_MAXIMUM} from './priceRules';
export type Currency = 'USD' | 'CNY';
export const CURRENCIES: Currency[] = ['USD', 'CNY'];
export const currencyPreference = createPreference<Currency>('preferred_currency', () => {
    try { return localStorage.getItem('preferred_currency') === 'CNY' ? 'CNY' : 'USD'; } catch { return 'USD'; }
}, value => localStorage.setItem('preferred_currency', value));
/** Ordered menu choices are a browser display preference, like saved time zones. */
export const currencyChoices = createPreference<Currency[]>('display-currency-choices-v1', () => {
    try {
        const saved: unknown = JSON.parse(localStorage.getItem('display-currency-choices-v1') || 'null');
        if (Array.isArray(saved)) {
            const valid = CURRENCIES.filter(code => saved.includes(code));
            if (valid.length) return [...new Set(saved.filter((code): code is Currency => valid.includes(code)))];
        }
    } catch { /* Keep the existing supported currencies. */ }
    return [...CURRENCIES];
}, value => localStorage.setItem('display-currency-choices-v1', JSON.stringify(value)));
export function saveCurrencyChoices(next: Currency[]) {
    const valid = [...new Set(next.filter(code => CURRENCIES.includes(code)))];
    if (!valid.length) return;
    if (!valid.includes(currencyPreference.get())) currencyPreference.set(valid[0]);
    currencyChoices.set(valid);
}
export interface ExchangeRates { USD_TO_CNY: number; USD_TO_EUR?: number; USD_TO_GBP?: number; last_updated: string | null; }
type RateSnapshot = { rates: ExchangeRates; fetchedAt: number; loading: boolean };
const EMPTY_RATES: ExchangeRates = { USD_TO_CNY: 0, last_updated: null };
const MAX_AGE = 24 * 60 * 60 * 1000;
const REFRESH_INTERVAL = 60 * 60 * 1000;
// Source-specific cache excludes the former logistics conversion endpoint's rates.
const CACHE_KEY = 'display-exchange-rates-frankfurter-ecb-v1';
export const EXCHANGE_RATE_SOURCE = {
    name: 'Frankfurter / ECB',
    url: 'https://api.frankfurter.dev/v2/rates?base=USD&quotes=CNY,EUR,GBP&providers=ecb',
} as const;
function validRateDate(date: unknown, now: number): date is string {
    if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date)) return false;
    const timestamp = Date.parse(`${date}T00:00:00Z`);
    // ECB reference dates can precede today across weekends and public holidays.
    return Number.isFinite(timestamp) && new Date(timestamp).toISOString().slice(0, 10) === date
        && timestamp <= now && now - timestamp < 7 * MAX_AGE;
}
function validRates(rates: ExchangeRates | undefined, now: number): boolean {
    return !!rates && Number.isFinite(rates.USD_TO_CNY) && rates.USD_TO_CNY > 0
        && validRateDate(rates.last_updated, now)
        && [rates.USD_TO_EUR, rates.USD_TO_GBP].every(rate => rate === undefined || (Number.isFinite(rate) && rate > 0));
}
export function parseExchangeRates(data: unknown, now = Date.now()): ExchangeRates | null {
    if (!Array.isArray(data) || data.length !== 3) return null;
    const rates: ExchangeRates = { ...EMPTY_RATES };
    const seen = new Set<string>();
    for (const row of data) {
        if (!row || row.base !== 'USD' || !['CNY', 'EUR', 'GBP'].includes(row.quote)
            || seen.has(row.quote) || typeof row.rate !== 'number' || !Number.isFinite(row.rate) || row.rate <= 0
            || !validRateDate(row.date, now) || (rates.last_updated && rates.last_updated !== row.date)) return null;
        seen.add(row.quote);
        rates[`USD_TO_${row.quote}` as 'USD_TO_CNY' | 'USD_TO_EUR' | 'USD_TO_GBP'] = row.rate;
        rates.last_updated = row.date;
    }
    return rates;
}
export const ratePreference = createPreference<RateSnapshot>(CACHE_KEY, () => {
    try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || 'null');
        const now = Date.now();
        if (validRates(cached?.rates, now) && Number.isFinite(cached.fetchedAt)
            && cached.fetchedAt <= now && cached.fetchedAt > now - MAX_AGE)
            return { rates: cached.rates, fetchedAt: cached.fetchedAt, loading: false };
    } catch { /* Invalid or unavailable local storage does not block the UI. */ }
    return { rates: EMPTY_RATES, fetchedAt: 0, loading: false };
}, value => localStorage.setItem(CACHE_KEY, JSON.stringify({ ...value, loading: false })));
let pending: Promise<void> | undefined;
let attemptedAt = 0;
export function loadExchangeRates(force = false): Promise<void> {
    if (pending) return pending;
    const now = Date.now();
    if (!force && (now - ratePreference.get().fetchedAt < REFRESH_INTERVAL || now - attemptedAt < 60_000)) return Promise.resolve();
    attemptedAt = now;
    const current = ratePreference.get();
    ratePreference.set(current.fetchedAt && now - current.fetchedAt < MAX_AGE && validRates(current.rates, now)
        ? { ...current, loading: true } : { rates: EMPTY_RATES, fetchedAt: 0, loading: true });
    pending = Promise.resolve().then(async () => {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8_000);
        try {
            // No credentials, business amounts or account data go to the public provider.
            const response = await fetch(EXCHANGE_RATE_SOURCE.url, {
                signal: controller.signal, credentials: 'omit', referrerPolicy: 'no-referrer', cache: 'no-cache',
            });
            if (!response.ok) return;
            const rates = parseExchangeRates(await response.json());
            if (rates) ratePreference.set({ rates, fetchedAt: Date.now(), loading: false });
        } catch { /* Keep valid cached rates; otherwise retain original currencies. */ }
        finally {
            clearTimeout(timeout);
            ratePreference.set({ ...ratePreference.get(), loading: false });
            pending = undefined;
        }
    });
    return pending;
}
let watchers = 0;
let stopWatching: (() => void) | undefined;
/** A single background refresh loop shared by mounted, active money consumers. */
export function watchExchangeRates(): () => void {
    watchers += 1;
    if (watchers === 1) {
        const refresh = () => { if (document.visibilityState === 'visible') void loadExchangeRates(); };
        const timer = window.setInterval(refresh, 60_000);
        document.addEventListener('visibilitychange', refresh);
        window.addEventListener('online', refresh);
        stopWatching = () => {
            window.clearInterval(timer);
            document.removeEventListener('visibilitychange', refresh);
            window.removeEventListener('online', refresh);
        };
    }
    void loadExchangeRates();
    return () => { if (--watchers === 0) { stopWatching?.(); stopWatching = undefined; } };
}
export function useMoneyPreference(load = true) {
    const active = useRouteActive();
    load = load && active;
    const currency = currencyPreference.useValue();
    const snapshot = ratePreference.useValue();
    useCriticalRead(load && !snapshot.fetchedAt && (snapshot.loading || !attemptedAt));
    useEffect(() => load ? watchExchangeRates() : undefined, [load]);
    return `${currency}:${snapshot.fetchedAt}`;
}
export function currencyCode(currency: string = 'USD'): string {
    const code = currency.trim().toUpperCase();
    return ({ RMB: 'CNY', '¥': 'CNY', '￥': 'CNY', '$': 'USD' } as Record<string,string>)[code] || code || 'USD';
}
export function currencyName(currency: string): string {
    const code = currencyCode(currency);
    return ({ USD: '美元', CNY: '人民币', EUR: '欧元', GBP: '英镑' } as Record<string, string>)[code] ?? code;
}
export function currencySymbol(currency: string): string {
    const code = currencyCode(currency);
    return ({ USD: '$', CNY: '¥', EUR: '€', GBP: '£' } as Record<string, string>)[code] ?? code;
}
function currencyRatio(from: string, to: string, snapshot = ratePreference.get()): [number,number] | null {
    from=currencyCode(from);to=currencyCode(to);
    if(from===to)return [1,1];
    if(!snapshot.fetchedAt || Date.now()-snapshot.fetchedAt>=MAX_AGE || !validRates(snapshot.rates,Date.now()))return null;
    const rates:Record<string,number|undefined>={USD:1,CNY:snapshot.rates.USD_TO_CNY,EUR:snapshot.rates.USD_TO_EUR,GBP:snapshot.rates.USD_TO_GBP};
    const source=rates[from],target=rates[to];
    return source && target && Number.isFinite(source) && Number.isFinite(target) && source>0 && target>0 ? [source,target] : null;
}
export function currencyFactor(from:string,to:string,snapshot=ratePreference.get()):number|null {
    const ratio=currencyRatio(from,to,snapshot);return ratio ? ratio[1]/ratio[0] : null;
}
function convertedDecimal(amount:Decimal.Value,from:string,to:string):Decimal|null {
    const ratio=currencyRatio(from,to);
    return ratio ? new Decimal(amount).times(ratio[1]).div(ratio[0]) : null;
}
export function convertMoney(amount:number,from:string,to:string):number|null {
    return Number.isFinite(amount) ? convertedDecimal(amount,from,to)?.toNumber()??null : null;
}
/** Convert exact source/target rates before rounding; inverse floating factors can ceil whole prices twice. */
export function moneyConvertedFixed(amount:Decimal.Value,from:string,to:string,places=MONEY_DECIMALS,rounding:Decimal.Rounding=Decimal.ROUND_HALF_UP):string {
    const value=convertedDecimal(amount,from,to);
    if(value==null)throw new Error('汇率不可用');
    return moneyFixed(value,places,rounding);
}
export function moneyDisplayCurrency(from: string): string {
    const selected = currencyPreference.get();
    return currencyFactor(from, selected) == null ? currencyCode(from) : selected;
}
/** Fixed currencies opt out of the display preference (documents/exports). */
export interface MoneyFormatOptions { currency?: string; numberOnly?: boolean; grouping?: boolean; decimals?: number; rounding?: Decimal.Rounding; salePrice?: boolean }
export const PRICE_ROUNDING = Decimal.ROUND_CEIL;
export function moneyFixed(amount: Decimal.Value, places=MONEY_DECIMALS, rounding:Decimal.Rounding=Decimal.ROUND_HALF_UP): string {
    return new Decimal(amount).toFixed(places,rounding);
}
export function moneyToDisplay(amount: string, factor: number, places=MONEY_DECIMALS, rounding:Decimal.Rounding=Decimal.ROUND_HALF_UP): string { return moneyFixed(new Decimal(amount).times(factor),places,rounding); }
export function roundMoney(amount: Decimal.Value): number { return Number(moneyFixed(amount)); }
export function moneyFromDisplay(amount: string, factor: number, places=MONEY_DECIMALS, rounding:Decimal.Rounding=Decimal.ROUND_HALF_UP): string {
    return new Decimal(amount).div(factor).toDecimalPlaces(places,rounding).toString();
}
export function productPriceError(value: unknown, allowZero=true): string | undefined {
    if(value==null || String(value).trim()==='')return undefined;
    const number=Number(value);
    if(typeof value==='boolean'||!Number.isFinite(number))return '请填写有效的价格';
    if(number<0||(!allowZero&&roundProductPrice(number)<=0))return allowZero?'价格不能小于 0':'价格必须大于 0';
    if(number>PRICE_MAXIMUM)return `价格不能超过 ${PRICE_MAXIMUM}`;
}
/** Every caller declares its source currency. Formatting never changes accounting values. */
export function formatMoney(amount: number | string | null | undefined, from: string = 'USD', options:MoneyFormatOptions={}): string {
    if (amount == null || String(amount).trim() === '' || !Number.isFinite(Number(amount))) return '';
    const to = options.currency ? currencyCode(options.currency) : moneyDisplayCurrency(from);
    const converted = convertedDecimal(amount, from, to);
    if(converted==null)throw new Error('汇率不可用');
    const fixed=moneyFixed(options.salePrice?converted.toDecimalPlaces(SALE_PRICE_DECIMALS,PRICE_ROUNDING):converted,options.decimals??MONEY_DECIMALS,options.rounding), negative=new Decimal(fixed).isNegative()&&!new Decimal(fixed).isZero();
    const [integer,fraction]=fixed.replace(/^-/, '').split('.');
    const number=`${options.grouping===false?integer:integer.replace(/\B(?=(\d{3})+(?!\d))/g,',')}${fraction===undefined?'':'.'+fraction}`;
    const symbol=currencySymbol(to);
    return `${negative?'-':''}${options.numberOnly?number:symbol===to?`${number} ${to}`:`${symbol}${number}`}`;
}

function requireConversion(amount:number, from:string, to:string) { const converted=convertMoney(amount,from,to); if(converted==null) throw new Error('汇率不可用'); return converted; }
function useCurrencyValue() {
    const selectedCurrency = currencyPreference.useValue();
    const snapshot = ratePreference.useValue();
    return useMemo(() => ({
        selectedCurrency, exchangeRates: snapshot.rates, isLoading: snapshot.loading,
        setCurrency: currencyPreference.set,
        toggleCurrency: () => { const choices = currencyChoices.get(); currencyPreference.set(choices[(choices.indexOf(selectedCurrency) + 1) % choices.length]); },
        convertToSelectedCurrency: (amount: number) => requireConversion(amount, 'USD', selectedCurrency),
        convertFromCurrency: (amount: number, from: Currency) => requireConversion(amount, from, 'USD'),
        formatPrice: (amount: number, options?: { showOriginal?: boolean }) => {
            const formatted = formatMoney(amount, 'USD');
            return options?.showOriginal && moneyDisplayCurrency('USD') !== 'USD' ? `${formatted} (${amount < 0 ? '-' : ''}$${Math.abs(amount).toFixed(MONEY_DECIMALS)})` : formatted;
        },
        refreshRates: () => loadExchangeRates(true),
    }), [selectedCurrency, snapshot]);
}
const CurrencyContext = createContext<ReturnType<typeof useCurrencyValue> | undefined>(undefined);
export function CurrencyProvider({ children }: { children: React.ReactNode }) {
    const value = useCurrencyValue();
    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}
export function useCurrency() {
    const context = useContext(CurrencyContext);
    const active = useRouteActive();
    useEffect(() => active ? watchExchangeRates() : undefined, [active]);
    useCriticalRead(active && !ratePreference.get().fetchedAt && (!!context?.isLoading || !attemptedAt));
    if (!context) throw new Error('useCurrency must be used within CurrencyProvider');
    return context;
}
export default useCurrency;

export function currencyOptions(source?: string, selected: string = currencyPreference.get()): DropdownItem[] {
    return currencyChoices.get().map(value => ({
        key: value, label: currencyName(value), selected: selected === value,
        disabled: source !== undefined && currencyFactor(source, value) == null,
        onClick: () => currencyPreference.set(value),
    }));
}


/** Canonical pot-price boundary, distinct from transaction amounts. */
export function roundProductPrice(value: Decimal.Value): number { return Number(moneyFixed(value,SALE_PRICE_DECIMALS,PRICE_ROUNDING)); }
export function formatProductPrice(value: number|string|null|undefined, from='USD', options:MoneyFormatOptions={}):string {
    return formatMoney(value,from,{...options,decimals:MONEY_DECIMALS,rounding:Decimal.ROUND_HALF_UP,salePrice:true});
}
